<template>
  <b-container fluid>
    <div class="iq-card mt-3">
      <div class="iq-card-header d-flex justify-content-between align-items-center">
        <div class="iq-header-title">
          <h4 class="card-title mb-1">RPM Classification Configuration</h4>
          <span class="text-muted" v-if="activeCompany">
            Active Company: <strong>{{ activeCompany.compName }}</strong>
          </span>
        </div>
        <div>
          <button class="btn btn-primary" @click="openAddForm">Add New</button>
        </div>
      </div>
      <div class="iq-card-body">

        <b-row class="mb-3">
          <b-col md="4">
            <b-form-group label="Filter by Asset" class="mb-0">
              <b-form-select v-model="selectedAsset" :options="assets" value-field="massId" text-field="massName"
                @change="getData">
                <template #first>
                  <b-form-select-option :value="null">-- All Assets --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Filter by Status" class="mb-0">
              <b-form-select v-model="selectedStatus" :options="statusOptions" @change="getData">
                <template #first>
                  <b-form-select-option :value="null">-- All Statuses --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Sort by Start Time" class="mb-0">
              <b-form-select v-model="sortOrder" :options="sortOptions" @change="getData"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-table striped hover :items="headers" :fields="fields" :busy="isLoading" class="text-center">
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>Loading...</strong>
            </div>
          </template>

          <template #cell(massName)="data">
            {{ data.item.mrohMass && data.item.mrohMass.massName ? data.item.mrohMass.massName : '-' }}
          </template>

          <template #cell(devcUniqueId)="data">
            {{ data.item.mrohDevc && data.item.mrohDevc.devcUniqueId ? data.item.mrohDevc.devcUniqueId : '-' }}
          </template>

          <template #cell(mrohStatus)="data">
            <b-form-checkbox switch v-model="data.item.mrohStatus" :value="1" :unchecked-value="0"
              @change="toggleStatus(data.item)">
              {{ data.item.mrohStatus === 1 ? 'Active' : 'Inactive' }}
            </b-form-checkbox>
          </template>

          <template #cell(actions)="data">
            <b-button variant="warning" size="sm" @click="openEditForm(data.item)" class="mr-2">
              Detail
            </b-button>
            <b-button v-if="data.item.mrohStatus !== 1" variant="danger" size="sm" @click="openDelete(data.item)">
              Delete
            </b-button>
          </template>
        </b-table>
      </div>
    </div>

    <!-- Modal Form -->
    <b-modal v-model="displayForm" :title="formTitle" hide-footer size="lg">
      <RpmClassificationForm :data="selectedItem" :is-edit="isEdit" @onSaved="onFormSaved"
        @onCancel="displayForm = false" />
    </b-modal>

    <!-- Modal Delete -->
    <b-modal v-model="displayDeletion" title="Confirm Deletion" @ok="onConfirmDeletion">
      <p>Are you sure you want to delete this item?</p>
    </b-modal>

    <!-- Modal Confirm Switch Active -->
    <b-modal v-model="displayToggleConfirm" title="Confirm Activation" @ok="onConfirmToggle" @cancel="onCancelToggle"
      @close="onCancelToggle">
      <p>There is already an active configuration for this Asset. Are you sure you want to deactivate the previous configuration and activate this one?</p>
    </b-modal>

    <!-- Modal Confirm Deactivation -->
    <b-modal v-model="displayDeactivationConfirm" title="Confirm Deactivation" @ok="onConfirmDeactivation" @cancel="onCancelDeactivation"
      @close="onCancelDeactivation">
      <p>Warning: Disabling this configuration will result in RPM data not being classified. Are you sure you want to proceed?</p>
    </b-modal>

  </b-container>
</template>

<script>
import RpmOperationHeaderSvc from '@/services/RpmOperationHeaderSvc';
import AssetSvc from '@/services/AssetSvc';
import RpmClassificationForm from './Form.vue';

export default {
  name: 'RpmClassificationList',
  components: {
    RpmClassificationForm,
  },
  data() {
    return {
      headers: [],
      assets: [],
      selectedAsset: null,
      selectedStatus: null,
      statusOptions: [
        { value: 1, text: 'Active' },
        { value: 0, text: 'Inactive' },
      ],
      sortOrder: 'DESC', // Default order
      sortOptions: [
        { value: 'DESC', text: 'Newest First' },
        { value: 'ASC', text: 'Oldest First' },
      ],
      fields: [
        { key: 'massName', label: 'Asset Name' },
        { key: 'devcUniqueId', label: 'Device Unique ID' },
        { key: 'mrohStartTime', label: 'Start Time' },
        { key: 'mrohStatus', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      isLoading: false,
      displayForm: false,
      displayDeletion: false,
      selectedItem: null,
      isEdit: false,

      // State for active status toggle confirmation
      displayToggleConfirm: false,
      itemToToggle: null,
      activeItemToDeactivate: null,

      // State for deactivation confirmation
      displayDeactivationConfirm: false,
      itemToDeactivate: null,
    };
  },
  computed: {
    formTitle() {
      return this.isEdit ? 'Detail RPM Classification' : 'Add RPM Classification';
    },
    activeCompany() {
      return this.$store.state.auth.activeCompany;
    },
  },
  mounted() {
    this.fetchAssets();
    this.getData();
  },
  methods: {
    async fetchAssets() {
      try {
        const response = await AssetSvc.getAssets();
        this.assets = response.data.data;
      } catch (error) {
        console.error(error);
      }
    },
    async getData() {
      this.isLoading = true;
      try {
        const params = {};
        if (this.activeCompany) {
          params['finds[mrohCompId]'] = this.activeCompany.compId;
        }
        if (this.selectedAsset) {
          params['finds[mrohMassId]'] = this.selectedAsset;
        }
        if (this.selectedStatus !== null) {
          params['finds[mrohStatus]'] = this.selectedStatus;
        }
        if (this.sortOrder) {
          params['orders[mrohStartTime]'] = this.sortOrder;
        }
        const response = await RpmOperationHeaderSvc.getRpmOperationHeaders(params);
        this.headers = response.data;
      } catch (error) {
        console.error('Failed to fetch RPM Operation Headers', error);
        this.$toast.error('Failed to fetch data.');
      } finally {
        this.isLoading = false;
      }
    },
    async toggleStatus(item) {
      if (item.mrohStatus === 1) {
        // Checking jika ada yang aktif di asset yang sama
        this.isLoading = true;
        try {
          const params = {
            'finds[mrohMassId]': item.mrohMassId,
            'finds[mrohStatus]': 1,
          };
          const response = await RpmOperationHeaderSvc.getRpmOperationHeaders(params);
          const activeOthers = response.data.filter((h) => h.mrohId !== item.mrohId);

          if (activeOthers.length > 0) {
            this.itemToToggle = item;
            this.activeItemToDeactivate = activeOthers[0]; // ambil yg pertama aktif
            this.displayToggleConfirm = true;
            this.isLoading = false;
            return; // stop disini tunggu confirm user
          }

          await this.executeToggle(item, 1);
        } catch (error) {
          item.mrohStatus = 0; // kembalikan switch ke off
          this.$toast.error('Gagal mengecek status konfigurasi aktif lainnya.');
        }
        this.isLoading = false;
      } else {
        // Simpan item sementara dan munculkan modal konfirmasi penonaktifan
        this.itemToDeactivate = item;
        this.displayDeactivationConfirm = true;
      }
    },
    async executeToggle(item, statusValue) {
      try {
        // Strip object relasi (mrohComp, mrohDevc, mrohMass) biar ga bikin error backend
        const payload = {
          mrohId: item.mrohId,
          mrohCompId: item.mrohCompId,
          mrohMassId: item.mrohMassId,
          mrohDevcId: item.mrohDevcId,
          mrohStartTime: item.mrohStartTime ? item.mrohStartTime.replace('T', ' ') : null,
          mrohStatus: statusValue,
        };

        await RpmOperationHeaderSvc.updateRpmOperationHeader(item.mrohId, payload);
        this.$toast.success('Status updated successfully.');

        // Update data aslinya supaya switch gak mental balik
        item.mrohStatus = statusValue;
      } catch (error) {
        item.mrohStatus = item.mrohStatus === 1 ? 0 : 1; // revert switch jika error
        this.$toast.error('Failed to update status.');
      }
    },
    async onConfirmToggle() {
      this.isLoading = true;
      try {
        // Matikan yang lama dulu (Hanya kirim field aslinya, jangan kirim object relasi)
        const oldPayload = {
          mrohId: this.activeItemToDeactivate.mrohId,
          mrohCompId: this.activeItemToDeactivate.mrohCompId,
          mrohMassId: this.activeItemToDeactivate.mrohMassId,
          mrohDevcId: this.activeItemToDeactivate.mrohDevcId,
          mrohStartTime: this.activeItemToDeactivate.mrohStartTime ? this.activeItemToDeactivate.mrohStartTime.replace('T', ' ') : null,
          mrohStatus: 0,
        };

        await RpmOperationHeaderSvc.updateRpmOperationHeader(oldPayload.mrohId, oldPayload);

        // Hidupkan yang baru
        await this.executeToggle(this.itemToToggle, 1);
      } catch (error) {
        this.itemToToggle.mrohStatus = 0;
        this.$toast.error('Gagal melakukan perpindahan status aktif.');
      } finally {
        this.displayToggleConfirm = false;
        this.isLoading = false;
        this.getData();
      }
    },
    onCancelToggle() {
      if (this.itemToToggle) {
        this.itemToToggle.mrohStatus = 0; // kembalikan switch secara visual
      }
      this.displayToggleConfirm = false;
    },
    async onConfirmDeactivation() {
      this.isLoading = true;
      try {
        if (this.itemToDeactivate) {
          await this.executeToggle(this.itemToDeactivate, 0);
        }
      } catch (error) {
        // Jika gagal API, kembalikan status ke 1 (Active)
        if (this.itemToDeactivate) this.itemToDeactivate.mrohStatus = 1;
        this.$toast.error('Failed to deactivate status.');
      } finally {
        this.itemToDeactivate = null;
        this.displayDeactivationConfirm = false;
        this.isLoading = false;
      }
    },
    onCancelDeactivation() {
      // Kembalikan switch ke posisi ON (Active) secara visual karena user membatalkan
      if (this.itemToDeactivate) {
        this.itemToDeactivate.mrohStatus = 1;
        this.itemToDeactivate = null;
      }
      this.displayDeactivationConfirm = false;
    },
    async onFormSaved(headerId) {
      // Fetch tabel di belakang layar
      await this.getData();

      // Cari item yang baru disave untuk di-load ulang ke form (agar form tetap terbuka di mode edit)
      const savedItem = this.headers.find((h) => h.mrohId === headerId);
      if (savedItem) {
        this.isEdit = true;
        this.selectedItem = { ...savedItem };
        this.displayForm = true; // memastikan modal tetap terbuka
      }
    },
    openAddForm() {
      this.isEdit = false;
      this.selectedItem = {};
      this.displayForm = true;
    },
    openEditForm(item) {
      this.isEdit = true;
      this.selectedItem = { ...item };
      this.displayForm = true;
    },
    openDelete(item) {
      this.selectedItem = item;
      this.displayDeletion = true;
    },
    async onConfirmDeletion() {
      if (!this.selectedItem) return;

      this.isLoading = true;
      try {
        await RpmOperationHeaderSvc.deleteRpmOperationHeader(this.selectedItem.mrohId);
        this.$toast.success('Item deleted successfully.');
        this.getData();
      } catch (error) {
        console.error('Failed to delete item', error);
        this.$toast.error('Failed to delete item.');
      } finally {
        this.isLoading = false;
        this.selectedItem = null;
        this.displayDeletion = false;
      }
    },
  },
};
</script>
